import { useState } from "react";
import { toast } from "sonner";
import { Undo2, Link2 } from "lucide-react";
import { inr, fmtDate, can, api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ReverseDialog } from "@/components/ReverseDialog";
import { LinkDialog } from "@/components/LinkDialog";
import { ReceiveDialog } from "@/components/ReceiveDialog";
import { AllocateDialog } from "@/components/AllocateDialog";

const BADGE = {
  Reversal: "bg-violet-50 text-violet-700 border-violet-200",
  Reversed: "bg-slate-100 text-slate-500 border-slate-300 line-through",
  "Excess / Credit": "bg-orange-50 text-orange-700 border-orange-200",
};

function CheckCell({ row }) {
  const { user, bump } = useAuth();
  const [busy, setBusy] = useState(false);
  const toggle = async () => {
    setBusy(true);
    try {
      await api.post(`/cash/${row._txn}/reconcile`, { reconciled: !row.reconciled });
      bump();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <td><input type="checkbox" className="h-4 w-4 accent-emerald-600" checked={!!row.reconciled} disabled={busy || !can(user, "entry:cash")}
      onChange={toggle} data-testid={`reconcile-${row._txn}`} /></td>
  );
}

function Cell({ c, v, row }) {
  if (c.type === "check") return <CheckCell row={row} />;
  if (c.type === "money") {
    const n = Number(v) || 0;
    return <td className={`num text-right ${n < 0 ? "text-red-600" : n === 0 ? "text-slate-300" : ""}`}>{v === "" || v == null ? "" : inr(v)}</td>;
  }
  if (c.type === "date") return <td className="num">{fmtDate(v)}</td>;
  if (c.type === "badge") return <td>{v ? <span className={`text-[11px] px-1.5 py-0.5 rounded border ${BADGE[v] || "bg-slate-50 border-slate-200"}`}>{v}</span> : ""}</td>;
  return <td className="max-w-[260px] truncate" title={v}>{v}</td>;
}

export function DataTable({ columns, rows, totals, onReverse, onLink, onReceive, onAllocate, testId }) {
  const { user } = useAuth();
  const canRev = (r) => onReverse && r._txn && !r._status && can(user, "reversal:request") && (user.role !== "staff" || r._created_by === user.id);
  const canLink = (r) => onLink && r._ledger === "cash" && !r._status && user.role === "admin";
  const hasAction = onReverse && rows.some((r) => r._txn);
  return (
    <div className="overflow-auto max-h-[62vh] border border-slate-200 rounded-xl bg-white" data-testid={testId}>
      <table className="dense-table">
        <thead><tr>{columns.map((c) => <th key={c.key} className={c.type === "money" ? "text-right" : ""}>{c.label}</th>)}{hasAction && <th />}</tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={columns.length + 1} className="text-center text-slate-400 py-10">No records for this period</td></tr>}
          {rows.map((r, i) => (
            <tr key={r._txn || i} data-testid={`${testId}-row-${r._txn || i}`} className={r._status === "Reversed" ? "opacity-60" : ""}>
              {columns.map((c) => <Cell key={c.key} c={c} v={r[c.key]} row={r} />)}
              {hasAction && (
                <td className="space-x-3">{canRev(r) && (
                  <button onClick={() => onReverse(r)} className="inline-flex items-center gap-1 text-[11px] text-violet-700 hover:underline" data-testid={`reverse-btn-${r._txn}`}>
                    <Undo2 className="h-3 w-3" /> Reverse
                  </button>
                )}{canLink(r) && (
                  <button onClick={() => onLink(r)} className="inline-flex items-center gap-1 text-[11px] text-sky-700 hover:underline" data-testid={`link-btn-${r._txn}`}>
                    <Link2 className="h-3 w-3" /> {r._fund_account_id ? "Re-link" : "Link A/c"}
                  </button>
                )}{onReceive && r._payout_pending && can(user, "entry:vi") && (
                  <button onClick={() => onReceive(r)} className="inline-flex items-center gap-1 text-[11px] text-emerald-700 hover:underline" data-testid={`receive-btn-${r._txn}`}>
                    Mark Received
                  </button>
                )}{onAllocate && r._rz_open && can(user, "entry:dse") && (
                  <button onClick={() => onAllocate(r)} className="inline-flex items-center gap-1 text-[11px] text-amber-700 hover:underline" data-testid={`allocate-btn-${r._txn}`}>
                    Allocate to DSEs
                  </button>
                )}</td>
              )}
            </tr>
          ))}
        </tbody>
        {totals && rows.length > 0 && (
          <tfoot><tr className="bg-slate-900 text-white font-semibold">
            {columns.map((c, i) => <td key={c.key} className={c.type === "money" ? "num text-right" : ""}>{i === 0 ? "TOTAL" : c.type === "money" && totals[c.key] !== undefined ? inr(totals[c.key]) : ""}</td>)}
            {hasAction && <td />}
          </tr></tfoot>
        )}
      </table>
    </div>
  );
}

export function ReportView({ report, testId = "report" }) {
  const [target, setTarget] = useState(null);
  const [linkTarget, setLinkTarget] = useState(null);
  const [recvTarget, setRecvTarget] = useState(null);
  const [allocTarget, setAllocTarget] = useState(null);
  if (!report) return null;
  return (
    <div className="space-y-5 fade-up">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid={`${testId}-summary`}>
        {report.summary.map((s) => (
          <div key={s.label} className="bg-white border border-slate-200 rounded-xl px-4 py-3">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">{s.label}</div>
            <div className={`mt-1 font-semibold ${s.type === "money" ? "num text-lg" : "text-sm"} ${s.type === "money" && s.value < 0 ? "text-red-600" : "text-slate-900"}`}>
              {s.type === "money" ? inr(s.value) : s.value}
            </div>
          </div>
        ))}
      </div>
      <DataTable columns={report.columns} rows={report.rows} totals={report.totals} onReverse={setTarget} onLink={setLinkTarget} onReceive={setRecvTarget} onAllocate={setAllocTarget} testId={`${testId}-table`} />
      {(report.extra_tables || []).map((t, i) => (
        <div key={t.title} className="space-y-2">
          <h3 className="font-display font-bold text-slate-800">{t.title}</h3>
          <DataTable columns={t.columns} rows={t.rows} testId={`${testId}-extra-${i}`} />
        </div>
      ))}
      <ReverseDialog target={target} onClose={() => setTarget(null)} />
      {linkTarget && <LinkDialog row={linkTarget} onClose={() => setLinkTarget(null)} />}
      {recvTarget && <ReceiveDialog row={recvTarget} onClose={() => setRecvTarget(null)} />}
      {allocTarget && <AllocateDialog row={allocTarget} onClose={() => setAllocTarget(null)} />}
    </div>
  );
}
